import { useState, useRef, useEffect } from "react";
import { ShoppingBag, X, GraduationCap, ArrowRight, GripVertical } from "lucide-react";

export interface BasketProgram {
    id: number;
    name: string;
    degree_type?: string;
    faculty?: string;
}

interface ProgramBasketWidgetProps {
    selectedProgramIds: number[];
    programs: BasketProgram[];
    maxSelectable: number;
    onRemove: (programId: number) => void;
    onReorder: (newIds: number[]) => void;
    onApplyNow: () => void;
    /** Optional extra class applied to the outer wrapper (fixed positioning layer). */
    className?: string;
}

export default function ProgramBasketWidget({
    selectedProgramIds,
    programs,
    maxSelectable,
    onRemove,
    onReorder,
    onApplyNow,
    className = "",
}: ProgramBasketWidgetProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const dragSourceIndex = useRef<number | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const touchDragActive = useRef(false);

    // Refs for callbacks used inside native listeners (avoid stale closure)
    const selectedProgramIdsRef = useRef(selectedProgramIds);
    const dragOverIndexRef = useRef(dragOverIndex);
    const onReorderRef = useRef(onReorder);
    selectedProgramIdsRef.current = selectedProgramIds;
    dragOverIndexRef.current = dragOverIndex;
    onReorderRef.current = onReorder;

    useEffect(() => {
        const list = listRef.current;
        if (!list) return;

        const onTouchMove = (e: TouchEvent) => {
            if (dragSourceIndex.current === null) return;
            touchDragActive.current = true;
            e.preventDefault(); // stops page scroll while dragging

            const touch = e.touches[0];
            const items = list.querySelectorAll<HTMLElement>("[data-drag-item]");
            let target: number | null = null;
            items.forEach((item, i) => {
                const mid = item.getBoundingClientRect().top + item.getBoundingClientRect().height / 2;
                if (touch.clientY < mid && target === null) target = i;
            });
            if (target === null) target = items.length - 1;
            list.dispatchEvent(new CustomEvent("drag-over-index", { detail: target }));
        };

        const onTouchEnd = () => {
            if (!touchDragActive.current) {
                dragSourceIndex.current = null;
                return;
            }
            const sourceIndex = dragSourceIndex.current;
            const targetIndex = dragOverIndexRef.current;
            if (sourceIndex !== null && targetIndex !== null && sourceIndex !== targetIndex) {
                const reordered = [...selectedProgramIdsRef.current];
                const [moved] = reordered.splice(sourceIndex, 1);
                reordered.splice(targetIndex, 0, moved);
                onReorderRef.current(reordered);
            }
            dragSourceIndex.current = null;
            touchDragActive.current = false;
            list.dispatchEvent(new CustomEvent("drag-over-index", { detail: null }));
        };

        const onDragOverIndex = (e: Event) => {
            setDragOverIndex((e as CustomEvent).detail);
        };

        list.addEventListener("touchmove", onTouchMove, { passive: false });
        list.addEventListener("touchend", onTouchEnd);
        list.addEventListener("drag-over-index", onDragOverIndex);

        return () => {
            list.removeEventListener("touchmove", onTouchMove);
            list.removeEventListener("touchend", onTouchEnd);
            list.removeEventListener("drag-over-index", onDragOverIndex);
        };
    }, [isOpen]); 

    if (selectedProgramIds.length === 0) return null;

    const selectedProgramObjects = selectedProgramIds
        .map((programId) => programs.find((program) => program.id === programId))
        .filter((program): program is BasketProgram => program !== undefined);

    const handleDragStart = (index: number) => {
        dragSourceIndex.current = index;
    };

    const handleDragOver = (event: React.DragEvent, index: number) => {
        event.preventDefault();
        setDragOverIndex(index);
    };

    const handleDrop = (targetIndex: number) => {
        const sourceIndex = dragSourceIndex.current;
        if (sourceIndex === null || sourceIndex === targetIndex) {
            setDragOverIndex(null);
            dragSourceIndex.current = null;
            return;
        }
        const reordered = [...selectedProgramIds];
        const [moved] = reordered.splice(sourceIndex, 1);
        reordered.splice(targetIndex, 0, moved);
        onReorder(reordered);
        dragSourceIndex.current = null;
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        dragSourceIndex.current = null;
        setDragOverIndex(null);
    };

    const handleTouchStart = (index: number) => {
        dragSourceIndex.current = index;
        touchDragActive.current = false;
    };

    return (
        <>
            {/* Backdrop — mobile only */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/30 z-40 md:hidden"
                    onClick={() => setIsOpen(false)}
                    aria-hidden="true"
                />
            )}

            <div className={`fixed bottom-20 right-4 md:bottom-8 md:right-8 z-50 flex flex-col items-end gap-3 ${className}`}>
                {/* Expanded panel */}
                {isOpen && (
                    <div
                        className="w-[calc(100vw-2rem)] md:w-96 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden
                                   animate-in slide-in-from-right-4 fade-in duration-200"
                    >
                        {/* Panel header */}
                        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-[#001B44]">
                            <div className="flex items-center gap-2">
                                <ShoppingBag className="h-4 w-4 text-white/80" />
                                <h3 className="text-sm font-black text-white uppercase tracking-widest">
                                    Your Selection
                                </h3>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-[11px] font-bold text-white/60 uppercase tracking-wide">
                                    {selectedProgramIds.length} / {maxSelectable}
                                </span>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                                    aria-label="Close basket"
                                >
                                    <X className="h-3.5 w-3.5 text-white" />
                                </button>
                            </div>
                        </div>

                        {/* Drag-to-reorder hint — only shown when >1 programs */}
                        {selectedProgramIds.length > 1 && (
                            <p className="text-[10px] text-slate-400 text-center px-4 pt-2.5 pb-1 font-medium">
                                Drag <GripVertical className="inline h-3 w-3 -mt-0.5" /> to reorder your preferences
                            </p>
                        )}

                        {/* Program list */}
                        <div ref={listRef} className="divide-y divide-slate-50 max-h-80 overflow-y-auto">
                            {selectedProgramObjects.map((program, index) => {
                                const isDraggingOver = dragOverIndex === index && dragSourceIndex.current !== index;
                                const isDragSource = dragSourceIndex.current === index;

                                return (
                                    <div
                                        key={program.id}
                                        data-drag-item
                                        draggable
                                        onDragStart={() => handleDragStart(index)}
                                        onDragOver={(event) => handleDragOver(event, index)}
                                        onDrop={() => handleDrop(index)}
                                        onDragEnd={handleDragEnd}
                                        onTouchStart={() => handleTouchStart(index)}
                                        className={`flex items-center gap-3 px-4 py-3.5 group transition-colors select-none
                                            ${isDragSource ? "opacity-40 bg-slate-50" : ""}
                                            ${isDraggingOver ? "bg-emerald-50 border-t-2 border-t-emerald-400" : "hover:bg-slate-50/60"}
                                        `}
                                    >
                                        {/* Rank badge */}
                                        <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 font-black text-sm bg-emerald-500 text-white">
                                            {selectedProgramIds.length > 1 ? (
                                                <span>{index + 1}</span>
                                            ) : (
                                                <GraduationCap className="h-4 w-4" />
                                            )}
                                        </div>

                                        {/* Program info */}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-black text-slate-800 truncate leading-tight">
                                                {program.name}
                                            </p>
                                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                                {program.degree_type && (
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">
                                                        {program.degree_type}
                                                    </span>
                                                )}
                                                {program.degree_type && program.faculty && (
                                                    <span className="text-[10px] text-slate-300">·</span>
                                                )}
                                                {program.faculty && (
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide truncate">
                                                        {program.faculty}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Drag handle */}
                                        <GripVertical className="h-4 w-4 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0 cursor-grab active:cursor-grabbing" />

                                        {/* Remove */}
                                        <button
                                            onClick={() => onRemove(program.id)}
                                            className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors shrink-0 text-slate-400"
                                            aria-label={`Remove ${program.name}`}
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Apply Now footer */}
                        <div className="px-4 py-4 bg-slate-50/60 border-t border-slate-100">
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    onApplyNow();
                                }}
                                disabled={selectedProgramIds.length === 0}
                                className="w-full h-12 bg-[#001B44] hover:bg-[#002b66] text-white font-black text-sm rounded-xl
                                           flex items-center justify-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Apply Now
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Trigger button (floating, always visible when ≥1 selected) */}
                <button
                    onClick={() => setIsOpen((prev) => !prev)}
                    className="relative w-14 h-14 rounded-2xl bg-[#001B44] hover:bg-[#002b66] text-white shadow-2xl
                               flex items-center justify-center transition-all active:scale-95"
                    aria-label={`Open program basket — ${selectedProgramIds.length} selected`}
                    title="View selected programs"
                >
                    <ShoppingBag className="h-6 w-6" />
                    {/* Count badge */}
                    <span
                        className="absolute -top-2 -right-2 min-w-[22px] h-[22px] rounded-full bg-emerald-500 text-white
                                   text-[11px] font-black flex items-center justify-center px-1 shadow-md border-2 border-white"
                    >
                        {selectedProgramIds.length}
                    </span>
                </button>
            </div>
        </>
    );
}
